import React, { useState, useMemo } from 'react';
import { UserInput } from '../types';
import SparkleDust from './SparkleDust';

interface StoryFormProps {
  onSubmit: (input: UserInput) => void;
  isBusy?: boolean;
  tokens: number;
  onGoToShop: () => void;
}

const THEMES = [
  { id: 'fairytale', name: 'Klasike', icon: <span className="text-3xl">🏰</span> },
  { id: 'adventure', name: 'Aventurë', icon: <span className="text-3xl">🧭</span> },
  { id: 'funny', name: 'Humor', icon: <span className="text-3xl">😂</span> },
  { id: 'sci-fi', name: 'Shkencë', icon: <span className="text-3xl">🚀</span> },
  { id: 'bedtime', name: 'Gjumë', icon: <span className="text-3xl">🌙</span> },
  { id: 'albanian_folklore', name: 'Folklor', icon: <span className="text-3xl">🇦🇱</span> },
] as const;

const AGE_CATEGORIES = [
  { id: '0-3', name: '0-3 vjeç' },
  { id: '3-6', name: '3-6 vjeç' },
  { id: '6-9', name: '6-9 vjeç' },
  { id: '9+', name: '9+ vjeç' },
] as const;

const MAGIC_STARTERS = [
  "Udhëtimi në hënë",
  "Kështjella e humbur",
  "Dragoi me flluska",
  "Miu i vogël në bibliotekë",
  "Robot i sjellshëm",
  "Ishulli i çokollatës",
  "Milingona atlete",
  "Pema që fliste",
  "Rrëshqitja në ylber",
  "Sekreti i oqeanit",
  "Kopshti magjik i luleve",
  "Udhëtimi me balonë",
  "Macja detektive",
  "Gara e breshkave fluturuese",
  "Kështjella e reve",
  "Mbretëria e lodrave",
  "Aventurë në xhungël",
  "Ylli që ra në tokë",
  "Libri i vjetër i magjisë",
  "Flutura me krahë kristali"
];

const StoryForm: React.FC<StoryFormProps> = ({ onSubmit, isBusy, tokens, onGoToShop }) => {
  const [characterName, setCharacterName] = useState('');
  const [referencePrompt, setReferencePrompt] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [voiceType, setVoiceType] = useState<'female' | 'male'>('female');
  const [storyType, setStoryType] = useState<typeof THEMES[number]['id']>('fairytale');
  const [ageCategory, setAgeCategory] = useState<typeof AGE_CATEGORIES[number]['id']>('3-6');

  const [isConfirming, setIsConfirming] = useState(false);

  const randomStarters = useMemo(() => {
    return [...MAGIC_STARTERS].sort(() => 0.5 - Math.random()).slice(0, 4);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize to max 800px while maintaining aspect ratio
          const MAX_SIZE = 800;
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with 0.8 quality to reduce size significantly
          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
          setPhoto(resizedBase64);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!characterName || !referencePrompt || isBusy || tokens < 1) return;
    
    setIsConfirming(true);
    setTimeout(() => {
      setIsConfirming(false);
      onSubmit({ characterName, referencePrompt, photoBase64: photo, voiceType, storyType, ageCategory });
    }, 800);
  };

  const hasNoTokens = tokens < 1;

  return (
    <div className={`glass-card rounded-[2rem] p-5 md:p-10 max-w-2xl mx-auto border border-white/10 transition-all ${isBusy ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="mb-6 text-center">
        <h2 className="text-3xl md:text-5xl font-bold text-white font-fairytale mb-1 tracking-wide text-glow-yellow">Krijo Përrallën</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 md:space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="flex items-center h-5 text-[11px] font-bold text-indigo-200 uppercase tracking-widest mb-1.5 ml-2">
              Emri i fëmijës
            </label>
            <input
              type="text" required disabled={isBusy} placeholder="Psh: Albani..."
              className="w-full h-[52px] px-4 rounded-xl bg-white/10 border border-white/20 outline-none text-white text-sm"
              value={characterName} onChange={(e) => setCharacterName(e.target.value)}
            />
          </div>
          <div>
            <label className="flex items-center gap-1 h-5 text-[11px] font-bold text-indigo-200 uppercase tracking-widest mb-1.5 ml-2">
              Lexon <span className="text-sm">📖</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setVoiceType('female')} className={`h-[52px] rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${voiceType === 'female' ? 'bg-yellow-400/20 border-yellow-400 text-yellow-100 shadow-lg' : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'}`}>
                <span className="text-xl">👩‍🦰</span> Mami
              </button>
              <button type="button" onClick={() => setVoiceType('male')} className={`h-[52px] rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${voiceType === 'male' ? 'bg-yellow-400/20 border-yellow-400 text-yellow-100 shadow-lg' : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'}`}>
                <span className="text-xl">🧔</span> Babi
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-[11px] font-bold text-indigo-200 uppercase tracking-widest mb-3 ml-2 flex items-center gap-1">
              Mosha e fëmijës <span className="text-sm">👶</span>
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {AGE_CATEGORIES.map((cat) => (
                <button key={cat.id} type="button" onClick={() => setAgeCategory(cat.id)} className={`h-[52px] rounded-xl border text-[10px] font-bold uppercase transition-all flex items-center justify-center ${ageCategory === cat.id ? 'bg-yellow-400/20 border-yellow-400 text-yellow-100 shadow-lg' : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'}`}>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-indigo-200 uppercase tracking-widest mb-3 ml-2 flex items-center gap-1">
              Zgjidh Temën <span className="text-sm">✨</span>
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
              {THEMES.map((theme) => (
                <button key={theme.id} type="button" onClick={() => setStoryType(theme.id)} className={`flex flex-col items-center p-2 rounded-xl border transition-all ${storyType === theme.id ? 'bg-yellow-400/20 border-yellow-400 text-yellow-100 shadow-lg' : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'}`}>
                  <span className="mb-1">{theme.icon}</span>
                  <span className="text-[9px] font-bold uppercase tracking-tighter truncate w-full text-center">{theme.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-bold text-indigo-200 uppercase tracking-widest mb-1.5 ml-2">Idea Magjike</label>
          <textarea
            required disabled={isBusy} placeholder="Shkruaj një ide..." rows={2}
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 outline-none text-white text-sm resize-none"
            value={referencePrompt} onChange={(e) => setReferencePrompt(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {randomStarters.map((starter, i) => (
              <button key={i} type="button" onClick={() => setReferencePrompt(starter)} className="text-[9.5px] bg-white/10 hover:bg-indigo-500/20 border border-white/20 rounded-full px-2.5 py-1 text-indigo-100/80 flex items-center gap-1 transition-colors">
                <span className="text-[11px]">✨</span> {starter}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <input type="file" id="file-upload" className="hidden" accept="image/*" onChange={handleFileChange} disabled={isBusy} />
          
          <label
            htmlFor="file-upload"
            className={`group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-300 ${photo ? 'border-green-500/50 bg-green-500/10' : 'border-[#4f5587] bg-[#21244d] hover:border-[#ffd700] hover:bg-[#282c5e]'} px-5 pb-6 pt-10`}
          >
            {photo ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/50 animate-in zoom-in">
                  <span className="text-3xl">✨</span>
                </div>
                <span className="text-green-400 font-bold uppercase text-xs tracking-widest">Fotoja u ngarkua!</span>
                <span className="text-white/40 text-[10px] uppercase tracking-tighter">Kliko për ta ndryshuar</span>
              </div>
            ) : (
              <>
                {/* Sketch Cluster */}
                <div className="relative mb-5 flex h-20 w-[120px] justify-center">
                  
                  {/* Left Sketch */}
                  <div className="absolute z-10 h-[60px] w-[50px] -translate-x-[30px] translate-y-[10px] -rotate-[15deg] rounded-md bg-[#f8f9fa] p-1 pb-3 shadow-lg transition-transform duration-300 group-hover:-translate-x-[40px] group-hover:translate-y-[5px] group-hover:-rotate-[25deg]">
                    <div className="flex h-full w-full items-center justify-center rounded border border-dashed border-[#caced1] text-[#a0a4a8]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                    </div>
                  </div>

                  {/* Right Sketch */}
                  <div className="absolute z-10 h-[60px] w-[50px] translate-x-[30px] translate-y-[10px] rotate-[15deg] rounded-md bg-[#f8f9fa] p-1 pb-3 shadow-lg transition-transform duration-300 group-hover:translate-x-[40px] group-hover:translate-y-[5px] group-hover:rotate-[25deg]">
                    <div className="flex h-full w-full items-center justify-center rounded border border-dashed border-[#caced1] text-[#a0a4a8]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
                      </svg>
                    </div>
                  </div>

                  {/* Center Sketch */}
                  <div className="absolute z-20 h-[68px] w-[56px] -translate-y-[5px] rotate-0 rounded-md bg-[#f8f9fa] p-1 pb-3 shadow-lg transition-transform duration-300 group-hover:-translate-y-[10px] group-hover:scale-105">
                    <div className="flex h-full w-full items-center justify-center rounded border border-dashed border-[#caced1] text-[#a0a4a8] transition-colors duration-300 group-hover:text-[#4f5587]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Text & Camera Icon */}
                <div className="flex items-center gap-2.5 text-sm font-semibold tracking-wide text-[#d1d5db]">
                  <svg className="h-5 w-5 text-[#ffd700]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  SHTO FOTON E FËMIJËS
                </div>
              </>
            )}
          </label>
        </div>

        {hasNoTokens ? (
          <div className="space-y-4">
             <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
               <p className="text-red-200 text-xs font-bold">Nuk keni xhevahire të mjaftueshme!</p>
             </div>
             <button 
               type="button" 
               onClick={onGoToShop}
               className="w-full sparkle-nav-btn active py-4 rounded-xl text-lg tracking-widest flex items-center justify-center gap-2"
             >
               <SparkleDust />
               <span className="text-xl">✨</span> Shto Xhevahire
             </button>
          </div>
        ) : (
          <div className="space-y-3">
            {isConfirming && (
              <div className="text-center text-yellow-400 text-xs font-black uppercase tracking-widest animate-bounce flex items-center justify-center gap-2">
                <span>💎</span> Duke përdorur 1 xhevahir...
              </div>
            )}
            <button 
              type="submit" 
              disabled={isBusy || isConfirming} 
              className={`w-full sparkle-nav-btn magic-yellow py-5 rounded-2xl text-2xl md:text-3xl lg:text-4xl tracking-[0.15em] disabled:opacity-50 flex items-center justify-center gap-3 shadow-2xl transition-all duration-500 ${isBusy ? 'animate-pulse' : 'hover:scale-[1.02]'}`}
            >
              <SparkleDust />
              {isBusy ? 'Magjia...' : 'Abrakadabra 🪄'}
            </button>
            <p className="text-center text-sm text-indigo-200/80 tracking-widest font-bold">
              ky shërbim kushton 1 xhevahir 💎
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default StoryForm;